import { useState } from "react";
import { useEdgeCandidates, useMaterialDetail } from "@/api/hooks/useCennik";
import { extractErrorMessage } from "@/api/client";
import { hlaskaNahrady, resolveAutoEdge } from "@/lib/autoEdge";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { STAV_ZLA_SIRKA } from "@/lib/stavHran";
import type { ZaoblenieState } from "@/lib/hrany";
import type { EdgeState, HranyState } from "@/components/dielec/HranyEditor";
import { HranaPickerDialog } from "@/components/dielec/HranyEditor";
import { SlotEdgePickerDialog } from "@/components/dielec/SlotEdgePickerDialog";

/**
 * Vizuálny obdĺžnik dielca so 4 hranami — DaexCUT screenshot 08.
 *
 * Proporcionálny obdĺžnik (max 240×240px) so 4 hranami
 * (X1 hore, X2 dole, Y1 vľavo, Y2 vpravo). Checkbox + ID vedľa
 * každej hrany. Stred: názov + rozmery + smer vlákien selector.
 */

export interface DielecVisualProps {
  xMm: number;
  yMm: number;
  nazov: string;
  smerVlakien: string;
  hrany: HranyState;
  onHranyChange: (h: HranyState) => void;
  onSmerChange: (s: string) => void;
  /** ID slotu — picker hrany použije iba slot-curated kandidátov. */
  slotId?: string | null;
  /** ID materiálu slotu — pre načítanie kandidátov. */
  slotMaterialId?: string | null;
  /** Projektová hrubá hrana (mm) — pre auto-default. */
  hrubaHranaMm?: string;
  /** ID varianty — picker ponúkne aj hrany už použité v zákazke. */
  variantaId?: string | null;
  /** Hrúbka dielca (mm) — pre rýchly prepínač hrúbky hrany. */
  zMm?: number;
  /** Celkové rozmery s nadmierou (napr. "725 × 505") — druhý riadok. */
  sNadmierou?: string | null;
  /** Materiál bez drevnej štruktúry (uni dekor) — X/Y prepínač disabled. */
  maStrukturu?: boolean;
  /** Stav uložených hrán dielca z backendu (KOLO 135) — `{ y1: "zla_sirka" }`. */
  stavHran?: Record<string, string>;
  /**
   * Doska je masív (KOLO 168) — neolepuje sa, namiesto hrán sa strany
   * zaobľujú. Rozhoduje backend (`dielec.masiv` / `material.masiv`).
   */
  masiv?: boolean;
  /** Zaoblené strany masívu — platí len pri `masiv`. */
  zaoblenie?: ZaoblenieState;
  onZaoblenieChange?: (z: ZaoblenieState) => void;
}

/**
 * Ktorá strana dielca je na nákrese hore/dole/vľavo/vpravo. Otočený
 * dielec (smer Y) sa točí proti smeru ručičiek: X1 hore → vľavo, Y1
 * vľavo → dole. Zdieľa ho detail aj náčrt v tabuľke (KOLO 180), aby
 * hrana bola na oboch tej istej strane.
 */
export function stranyNakresu(otoceny: boolean): {
  hore: keyof HranyState;
  dole: keyof HranyState;
  vlavo: keyof HranyState;
  vpravo: keyof HranyState;
} {
  return otoceny
    ? { hore: "y2", dole: "y1", vlavo: "x1", vpravo: "x2" }
    : { hore: "x1", dole: "x2", vlavo: "y1", vpravo: "y2" };
}

/** Voľby rýchleho prepínača hrúbky hrany. */
export const QUICK_HRUBKY = ["0.5", "1", "2"] as const;

/** Zaradenie reálnej hrúbky hrany do bucketu prepínača. */
export function hrubkaBucket(h: number | null): string | null {
  if (h == null) return null;
  if (h <= 0.6) return "0.5";
  if (h <= 1.2) return "1";
  return "2";
}

/**
 * Priradená hrana má inú hrúbku, než hovorí chip, na ktorom svieti.
 *
 * Dekor bez 0,5 mm dostane 0,8 (KOLO 103) a tá padne do bucketu „1",
 * takže by chip tvrdil číslo, ktoré na dielci nie je. Odvodzuje sa to
 * z hrúbky priradenej hrany — nikde sa nič neukladá, takže to platí aj
 * po návrate k dielcu a zmenou hrany to zmizne.
 */
export function jeNahradnaHrubka(h: number | null): boolean {
  const bucket = hrubkaBucket(h);
  if (h == null || bucket == null) return false;
  return Math.abs(h - parseFloat(bucket)) > 0.01;
}

/** Hrúbka čiary hrany v px podľa bucketu (0.5 → 3, 1 → 5, 2 → 8). */
const LINE_PX: Record<string, number> = { "0.5": 3, "1": 5, "2": 8 };

/**
 * Farby hrán podľa hrúbky — čiara na diagrame aj aktívny čip prepínača
 * majú rovnakú farbu, hrúbku hrany tak vidno na prvý pohľad.
 */
export const HRANA_FARBY: Record<string, { line: string; chip: string }> = {
  "0.5": { line: "bg-sky-500", chip: "bg-sky-500 border-sky-500 text-white" },
  "1": {
    line: "bg-violet-500",
    chip: "bg-violet-500 border-violet-500 text-white",
  },
  "2": {
    line: "bg-emerald-600",
    chip: "bg-emerald-600 border-emerald-600 text-white",
  },
};

/**
 * Znenie zrušenej legendy — do KOLA 66 to bol samostatný riadok pod
 * nákresom, teraz je to `title` rámu (riadok bral výšku, ktorú nákres
 * nemal odkiaľ vziať).
 */
const LEGENDA_HRAN =
  "Farba čiary = hrúbka hrany: 0.5 mm modrá · 1 mm fialová · 2 mm zelená";

/** Legenda nákresu masívu (KOLO 168) — namiesto farieb hrán. */
const LEGENDA_ZAOBLENIA =
  "Masív sa neolepuje — čiarkovaná čiara = strana zaoblená na olepovačke";

/** Žiadna strana zaoblená — keď volajúci zaoblenie nepošle. */
const BEZ_ZAOBLENIA: ZaoblenieState = { x1: false, x2: false, y1: false, y2: false };

/** Farba zaoblenia — čiara aj zapnutý prepínač, ako farby hrán. */
export const ZAOBLENIE_FARBA = {
  line: "border-amber-500",
  chip: "bg-amber-500 border-amber-500 text-white",
};

/**
 * Čiara zapnutej hrany na strane obdĺžnika — hrubšia hrana (0.5/1/2 mm)
 * = hrubšia čiara, farba podľa hrúbky. Auto hrana bez priradeného
 * materiálu = tenká modrá.
 */
function HranaLine({
  side,
  pos,
  edge,
  hrubka,
}: {
  side: "x1" | "x2" | "y1" | "y2";
  /** Strana obdĺžnika, na ktorej čiara leží — pri smere „Y" je diagram
   * otočený o 90°, takže hrana X1 môže ležať vľavo. */
  pos: "top" | "bottom" | "left" | "right";
  edge: EdgeState;
  hrubka: number | null;
}): JSX.Element | null {
  const enabled = edge.auto || !!edge.id;
  if (!enabled) return null;
  const bucket = hrubkaBucket(hrubka) ?? "0.5";
  const px = LINE_PX[bucket] ?? 3;
  const styly: Record<typeof pos, React.CSSProperties> = {
    top: { top: 0, left: 0, right: 0, height: px },
    bottom: { bottom: 0, left: 0, right: 0, height: px },
    left: { top: 0, bottom: 0, left: 0, width: px },
    right: { top: 0, bottom: 0, right: 0, width: px },
  };
  return (
    <div
      data-testid={`hrana-line-${side}`}
      className={cn(
        "absolute z-[5] rounded-sm pointer-events-none",
        HRANA_FARBY[bucket]?.line ?? "bg-sky-500",
      )}
      style={styly[pos]}
      title={`Hrana ${side.toUpperCase()} — ${bucket} mm`}
    />
  );
}

/**
 * Čiarkovaná čiara zaoblenej strany masívu (KOLO 168) — iná ako plná
 * čiara hrany, aby sa zaoblenie s hranou nepomýlilo.
 */
function ZaoblenieLine({
  side,
  pos,
  zapnute,
}: {
  side: "x1" | "x2" | "y1" | "y2";
  pos: "top" | "bottom" | "left" | "right";
  zapnute: boolean;
}): JSX.Element | null {
  if (!zapnute) return null;
  const styly: Record<typeof pos, React.CSSProperties> = {
    top: { top: 0, left: 0, right: 0, borderTopWidth: 3, borderTopStyle: "dashed" },
    bottom: {
      bottom: 0, left: 0, right: 0, borderBottomWidth: 3, borderBottomStyle: "dashed",
    },
    left: { top: 0, bottom: 0, left: 0, borderLeftWidth: 3, borderLeftStyle: "dashed" },
    right: {
      top: 0, bottom: 0, right: 0, borderRightWidth: 3, borderRightStyle: "dashed",
    },
  };
  return (
    <div
      data-testid={`zaoblenie-line-${side}`}
      className={cn("absolute z-[5] pointer-events-none", ZAOBLENIE_FARBA.line)}
      style={styly[pos]}
      title={`Zaoblenie ${side.toUpperCase()}`}
    />
  );
}

/**
 * Náhľad má JEDNOTNÚ veľkosť — pevný rám FRAME_W×FRAME_H, do ktorého
 * sa obdĺžnik vykreslí s pomerom strán OBMEDZENÝM do pásma
 * [RATIO_MIN, RATIO_MAX]. Používateľ tak vidí, ktorá strana je dlhšia,
 * ale detail panel pri prepínaní dielcov neskáče (500×200 aj 100×300
 * zaberú rovnaký priestor).
 */
// 264, nie 280: bočné stĺpce Y1/Y2 sú od KOLA 183 o 8 px širšie (popis
// hrany vodorovne), stĺpec nákresu tak ostal široký 412 px
export const FRAME_W = 264;
export const FRAME_H = 168;
const RATIO_MIN = 0.6;
const RATIO_MAX = 2.2;

/**
 * Prirodzená výška nákresu: popisok `h-5` + rám + popisok + 2× `gap-0.5`.
 *
 * Je to **rozpočet, nie dekorácia** — pás detailu má pevných 313 px a keď
 * nákres presiahne to, čo mu po hlavičke zostane, stĺpec začne scrollovať
 * (a keďže `overflow-y: auto` vynúti aj `overflow-x: auto`, hneď za tým
 * pribudne aj vodorovný scrollbar). Do KOLA 66 bola rezerva 3 px a user to
 * na svojom okne prevážil. Invariant stráži `DielecDetailPanel.test.tsx`.
 */
export const NAKRES_H = 20 + 2 + FRAME_H + 2 + 20;

function computeRect(xMm: number, yMm: number): { w: number; h: number } {
  // Dielec bez rozmerov — obdĺžnik v proporcii rámu, nech miesto nezíva.
  if (xMm <= 0 || yMm <= 0) return { w: 224, h: 140 };
  const ratio = Math.min(RATIO_MAX, Math.max(RATIO_MIN, xMm / yMm));
  // Zmestiť do FRAME_W × FRAME_H pri zachovaní pomeru. Staršia verzia
  // počítala `w = h * ratio` bez stropu, takže pri pomere nad ~1.67 obdĺžnik
  // vytŕčal z rámu (pri max 2.2 až 396 px do rámu širokého 300) a prekrýval
  // popisky hrán.
  const w = Math.min(FRAME_W, FRAME_H * ratio);
  return { w: Math.round(w), h: Math.round(w / ratio) };
}

export function DielecVisual({
  xMm,
  yMm,
  nazov,
  smerVlakien,
  hrany,
  onHranyChange,
  onSmerChange,
  slotId,
  slotMaterialId,
  hrubaHranaMm,
  variantaId,
  zMm,
  sNadmierou,
  maStrukturu = true,
  stavHran,
  masiv = false,
  zaoblenie,
  onZaoblenieChange,
}: DielecVisualProps): JSX.Element {
  // Smer „Y" = vlákno po šírke → dielec leží na tabuli otočený o 90°.
  // Diagram to zobrazí verne: obdĺžnik, rozmery aj hrany sa otočia
  // (rotácia proti smeru ručičiek: X1 hore → vľavo, Y1 vľavo → dole).
  const otoceny = maStrukturu && smerVlakien === "Y";
  const [dispX, dispY] = otoceny ? [yMm, xMm] : [xMm, yMm];
  const { w, h } = computeRect(dispX, dispY);
  const [picker, setPicker] = useState<keyof HranyState | null>(null);
  // Rozloženie hrán okolo obdĺžnika podľa orientácie
  const { hore, dole, vlavo, vpravo } = stranyNakresu(otoceny);

  function setEdge(key: keyof HranyState, edge: EdgeState): void {
    onHranyChange({ ...hrany, [key]: edge });
  }

  // Hrany materiálu (detegované, kurácia, override slotu) — hrana mimo
  // nich je vybratá ručne a zmena materiálu ju nechá (KOLO 177). Ten istý
  // dopyt ako okno výberu hrany, takže sa načíta raz.
  const kandidati = useEdgeCandidates(
    !masiv && slotMaterialId ? slotMaterialId : undefined,
    hrubaHranaMm,
    slotId ?? undefined,
  );
  const pool = kandidati.data?.data
    ? new Set([
        ...(kandidati.data.data.candidates ?? []).map((c) => c.edge.id),
        ...(kandidati.data.data.pool_ids ?? []),
      ])
    : null;
  function rucne(key: keyof HranyState): boolean {
    const id = hrany[key].id;
    return !!pool && !!id && !hrany[key].auto && !pool.has(id);
  }

  const zaobl = zaoblenie ?? BEZ_ZAOBLENIA;
  /** Masív (KOLO 168): strana sa namiesto hrany zaoblí — prepínač. */
  function prepniZaoblenie(key: keyof HranyState): void {
    onZaoblenieChange?.({ ...zaobl, [key]: !zaobl[key] });
  }

  function toggleEdge(key: keyof HranyState): void {
    const e = hrany[key];
    if (e.auto || e.id) {
      setEdge(key, { id: null, auto: false });
    } else {
      setEdge(key, { id: null, auto: true });
    }
  }

  /**
   * Rýchly prepínač hrúbky hrany — auto-priradí hranu materiálu.
   * Preklad mm → hrana je v `lib/autoEdge.ts`, aby ho hromadná úprava
   * viacerých dielcov nemusela implementovať druhý raz.
   */
  async function quickAssign(
    key: keyof HranyState,
    hranaMm: string,
  ): Promise<void> {
    if (!slotId) return;
    try {
      const edge = await resolveAutoEdge(slotId, zMm ?? null, hranaMm);
      if (!edge) {
        // Jediný stav, kedy sa naozaj nedá nič priradiť — dekor nemá
        // v potrebnej šírke žiadnu hranu.
        toast({
          variant: "destructive",
          title: `Hrana ${hranaMm} mm nie je v ponuke materiálu`,
          description: "Pridaj ju v materiáli cez + Pridať z cenníka.",
        });
        return;
      }
      setEdge(key, { id: edge.id, auto: false });
      if (edge.nahradena) {
        // Nie `destructive` — nič sa nepokazilo, hrana je priradená.
        toast({
          title: "Použitá najbližšia hrúbka",
          description: hlaskaNahrady(hranaMm, edge.hrubkaMm),
        });
      }
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri priraďovaní hrany",
        description: extractErrorMessage(err),
      });
    }
  }

  const quickEnabled = !!slotId;

  // Info o hranách pre vizualizáciu čiar na stranách obdĺžnika
  const edgeInfo = {
    x1: useEdgeInfo(hrany.x1),
    x2: useEdgeInfo(hrany.x2),
    y1: useEdgeInfo(hrany.y1),
    y2: useEdgeInfo(hrany.y2),
  };

  return (
    <div className="flex flex-col items-center gap-0.5">
      {/* horná hrana (X1; pri otočenom dielci Y2) — masív: zaoblenie */}
      {masiv ? (
        <ZaoblenieLabel
          label={hore.toUpperCase()}
          zapnute={zaobl[hore]}
          onToggle={() => prepniZaoblenie(hore)}
        />
      ) : (
        <HEdgeLabel
          label={hore.toUpperCase()}
          edge={hrany[hore]}
          onToggle={() => toggleEdge(hore)}
          onPick={() => setPicker(hore)}
          onQuick={quickEnabled ? (mm) => void quickAssign(hore, mm) : undefined}
          stav={stavHran?.[hore]}
          rucne={rucne(hore)}
        />
      )}

      <div className="flex items-center gap-0.5">
        {/* ľavá hrana (Y1; pri otočenom dielci X1) */}
        {masiv ? (
          <ZaoblenieLabel
            label={vlavo.toUpperCase()}
            zapnute={zaobl[vlavo]}
            onToggle={() => prepniZaoblenie(vlavo)}
            vertical
          />
        ) : (
          <VEdgeLabel
            label={vlavo.toUpperCase()}
            edge={hrany[vlavo]}
            onToggle={() => toggleEdge(vlavo)}
            onPick={() => setPicker(vlavo)}
            onQuick={quickEnabled ? (mm) => void quickAssign(vlavo, mm) : undefined}
            stav={stavHran?.[vlavo]}
            rucne={rucne(vlavo)}
            strana="vlavo"
          />
        )}

        {/* Pevný rám — obdĺžnik dielca vycentrovaný, panel neskáče */}
        <div
          className="flex items-center justify-center"
          style={{ width: FRAME_W, height: FRAME_H }}
          title={masiv ? LEGENDA_ZAOBLENIA : LEGENDA_HRAN}
        >
        <div
          className="relative border-2 border-foreground/40 bg-background-alt/60 flex items-center justify-center rounded-md shadow-sm"
          style={{ width: w, height: h, minWidth: w, minHeight: h }}
        >
          {/* Vlákno tabule beží vodorovne — dielec so smerom X aj otočený
              dielec so smerom Y majú vlákno v zobrazení vodorovné. */}
          {maStrukturu && (smerVlakien === "X" || smerVlakien === "Y") && (
            <GrainLines dir="h" w={w} h={h} />
          )}

          {/* Zapnuté hrany ako čiary — hrúbka podľa 0.5/1/2 mm; masív
              hrany nemá, kreslia sa zaoblené strany (KOLO 168) */}
          {masiv ? (
            <>
              <ZaoblenieLine side={hore} pos="top" zapnute={zaobl[hore]} />
              <ZaoblenieLine side={dole} pos="bottom" zapnute={zaobl[dole]} />
              <ZaoblenieLine side={vlavo} pos="left" zapnute={zaobl[vlavo]} />
              <ZaoblenieLine side={vpravo} pos="right" zapnute={zaobl[vpravo]} />
            </>
          ) : (
            <>
              <HranaLine side={hore} pos="top" edge={hrany[hore]} hrubka={edgeInfo[hore].hrubka} />
              <HranaLine side={dole} pos="bottom" edge={hrany[dole]} hrubka={edgeInfo[dole].hrubka} />
              <HranaLine side={vlavo} pos="left" edge={hrany[vlavo]} hrubka={edgeInfo[vlavo].hrubka} />
              <HranaLine side={vpravo} pos="right" edge={hrany[vpravo]} hrubka={edgeInfo[vpravo].hrubka} />
            </>
          )}

          <div className="relative z-10 text-center px-1">
            <div className="text-xs font-medium truncate max-w-[240px]">
              {nazov || "—"}
            </div>
            <div className="text-xs font-mono text-muted-foreground">
              {dispX > 0 && dispY > 0 ? `${dispX} × ${dispY}` : "—"}
            </div>
            {sNadmierou ? (
              <div
                className="text-[10px] font-mono text-muted-foreground/70"
                title="Celkové rozmery s nadmierou"
              >
                prírez {sNadmierou}
              </div>
            ) : null}
            {maStrukturu ? (
              <div className="flex items-center justify-center gap-0.5 mt-0.5">
                {(["X", "Y", "0"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onSmerChange(s)}
                    title={
                      s === "0"
                        ? "Bez smeru — dielec sa pri poreze smie otáčať"
                        : s === "X"
                          ? "Vlákno po dĺžke (X)"
                          : "Vlákno po šírke (Y) — dielec sa reže otočený"
                    }
                    className={
                      "px-1.5 py-0 rounded text-[10px] font-mono border transition-colors " +
                      (smerVlakien === s
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-input hover:bg-accent text-muted-foreground")
                    }
                  >
                    {s === "0" ? "∅" : s}
                  </button>
                ))}
              </div>
            ) : (
              <div
                className="mt-0.5 text-[10px] text-muted-foreground/70"
                title="Materiál bez drevnej štruktúry — smer vlákien sa nenastavuje, dielec sa pri poreze smie otáčať"
              >
                uni — bez štruktúry
              </div>
            )}
          </div>
        </div>
        </div>

        {/* pravá hrana (Y2; pri otočenom dielci X2) */}
        {masiv ? (
          <ZaoblenieLabel
            label={vpravo.toUpperCase()}
            zapnute={zaobl[vpravo]}
            onToggle={() => prepniZaoblenie(vpravo)}
            vertical
          />
        ) : (
          <VEdgeLabel
            label={vpravo.toUpperCase()}
            edge={hrany[vpravo]}
            onToggle={() => toggleEdge(vpravo)}
            onPick={() => setPicker(vpravo)}
            onQuick={quickEnabled ? (mm) => void quickAssign(vpravo, mm) : undefined}
            stav={stavHran?.[vpravo]}
            rucne={rucne(vpravo)}
            strana="vpravo"
          />
        )}
      </div>

      {/* dolná hrana (X2; pri otočenom dielci Y1) */}
      {masiv ? (
        <ZaoblenieLabel
          label={dole.toUpperCase()}
          zapnute={zaobl[dole]}
          onToggle={() => prepniZaoblenie(dole)}
        />
      ) : (
        <HEdgeLabel
          label={dole.toUpperCase()}
          edge={hrany[dole]}
          onToggle={() => toggleEdge(dole)}
          onPick={() => setPicker(dole)}
          onQuick={quickEnabled ? (mm) => void quickAssign(dole, mm) : undefined}
          stav={stavHran?.[dole]}
          rucne={rucne(dole)}
        />
      )}

      {/* Legenda farieb hrán tu bola ako vlastný riadok — zmazaná v KOLE 66.
          Brala ~13 px z 3 px rezervy nákresu, takže stĺpec začínal
          scrollovať; mapa farba → mm je aj tak na chipoch pri hranách
          (aktívny má farbu z `HRANA_FARBY[mm].chip`) a plné znenie je
          v `title` rámu. */}

      {/* Masív hrany nemá — výber hrany preň netreba ani načítať */}
      {masiv ? null : slotMaterialId ? (
        <SlotEdgePickerDialog
          open={!!picker}
          slotId={slotId ?? undefined}
          slotMaterialId={slotMaterialId}
          hrubaHranaMm={hrubaHranaMm}
          variantaId={variantaId ?? undefined}
          currentId={picker ? hrany[picker].id : null}
          zMm={zMm}
          onClose={() => setPicker(null)}
          onSelect={(id) => {
            if (picker) setEdge(picker, { id, auto: false });
            setPicker(null);
          }}
        />
      ) : (
        <HranaPickerDialog
          open={!!picker}
          currentId={picker ? hrany[picker].id : null}
          onClose={() => setPicker(null)}
          onSelect={(id) => {
            if (picker) setEdge(picker, { id, auto: false });
            setPicker(null);
          }}
        />
      )}
    </div>
  );
}

function GrainLines({
  dir,
  w,
  h,
}: {
  dir: "h" | "v";
  w: number;
  h: number;
}): JSX.Element {
  const count = dir === "h" ? Math.floor(h / 10) : Math.floor(w / 10);
  return (
    <div
      className={
        "absolute inset-0 overflow-hidden opacity-15 pointer-events-none" +
        (dir === "v" ? " flex" : "")
      }
    >
      {Array.from({ length: count }).map((_, i) =>
        dir === "h" ? (
          <div
            key={i}
            className="border-t border-foreground"
            style={{ marginTop: i === 0 ? 6 : 10 }}
          />
        ) : (
          <div
            key={i}
            className="border-l border-foreground h-full"
            style={{ marginLeft: i === 0 ? 6 : 10 }}
          />
        ),
      )}
    </div>
  );
}

/**
 * Prepínač zaoblenia strany masívu (KOLO 168) — na mieste checkboxu, čipov
 * a kódu hrany. Rozmery sú tie isté ako pri popiskoch hrán (`h-5`,
 * `FRAME_H`), takže nákres pri prepnutí slotu na masív neskáče.
 */
function ZaoblenieLabel({
  label,
  zapnute,
  onToggle,
  vertical = false,
}: {
  label: string;
  zapnute: boolean;
  onToggle: () => void;
  vertical?: boolean;
}): JSX.Element {
  const prepinac = (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={zapnute}
      aria-label={`Zaoblenie ${label}`}
      title={
        zapnute
          ? `${label} sa zaobľuje na olepovačke — klik: bez zaoblenia`
          : `Zaobliť ${label} na olepovačke (masív sa neolepuje)`
      }
      className={cn(
        "rounded border px-1 py-0 text-[9px] leading-4 transition-colors",
        zapnute
          ? ZAOBLENIE_FARBA.chip
          : "border-input text-muted-foreground hover:bg-accent",
      )}
      style={vertical ? { writingMode: "vertical-lr" } : undefined}
    >
      zaoblenie
    </button>
  );
  if (vertical) {
    return (
      <div
        className="flex w-12 flex-col items-center justify-center gap-0.5"
        style={{ height: FRAME_H }}
        data-testid={`edge-label-${label.toLowerCase()}`}
      >
        <span className="text-[10px] font-mono text-muted-foreground">
          {label}
        </span>
        {prepinac}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 h-5">
      <span className="text-[10px] font-mono text-muted-foreground w-5">
        {label}
      </span>
      {prepinac}
    </div>
  );
}

/**
 * Vodorovný popisok X1/X2 pri ráme — checkbox, chipy hrúbky a kód materiálu.
 * `h-5` je pevné, takže tento popisok výšku nikdy nemenil.
 */
function HEdgeLabel({
  label,
  edge,
  onToggle,
  onPick,
  onQuick,
  stav,
  rucne = false,
}: {
  label: string;
  edge: EdgeState;
  onToggle: () => void;
  onPick: () => void;
  onQuick?: (mm: string) => void;
  /** Stav uloženej hrany strany z backendu (`zla_sirka`…), KOLO 135. */
  stav?: string;
  /** Hrana je mimo hrán materiálu — vybratá ručne (KOLO 177). */
  rucne?: boolean;
}): JSX.Element {
  const enabled = edge.auto || !!edge.id;
  const info = useEdgeInfo(edge);
  const st = stavStitku(stav);

  return (
    <div className="flex items-center gap-1.5 h-5">
      <input
        type="checkbox"
        checked={enabled}
        onChange={onToggle}
        title="Zapnúť / vypnúť hranu"
        aria-label={`Zapnúť / vypnúť hranu ${label}`}
        className="h-3.5 w-3.5 accent-primary cursor-pointer"
      />
      <span className="text-[10px] font-mono text-muted-foreground w-5">
        {label}
      </span>
      {enabled && onQuick ? (
        <ThicknessQuick label={label} current={info.hrubka} onPick={onQuick} />
      ) : null}
      {enabled ? (
        <button
          type="button"
          onClick={onPick}
          className={cn(
            "max-w-[240px] truncate text-xs font-mono hover:underline cursor-pointer",
            st.trieda,
          )}
          title={`${st.title}${info.label}${rucne ? RUCNE_TITLE : ""} — klik: vybrať hranu`}
        >
          {stav === STAV_ZLA_SIRKA ? "! " : null}
          {info.label}
          {rucne ? " · ručne" : null}
        </button>
      ) : null}
      {enabled ? <HranaOdkaz odkaz={info.odkaz} label={label} /> : null}
    </div>
  );
}

/**
 * Zvislý popisok Y1/Y2 — checkbox, chipy hrúbky a kód materiálu priamo pri
 * hrane, aby sa hrúbka nastavovala tam, kam sa pozeráš.
 *
 * Popis hrany má **vlastný stĺpec na vonkajšej strane** (vľavo pri Y1,
 * vpravo pri Y2), otočený o 90° cez celú výšku rámu. User (KOLO 183):
 * *„pri výbere hrán, čo sú naľavo a napravo, nevidno nadpis … sprav, že
 * jeden riadok je výber hrán, druhý je popis"* — dovtedy bol popis pod
 * chipmi a orezaný na „42 × 1 …". Vodorovne pod chipmi (prvý pokus)
 * user prečiarkol: *„aby bol akoby pod tými tlačidlami, tam kde je
 * červená čiara, že je to otočené o 90 stupňov, nevadí"*.
 *
 * Výška `FRAME_H` je to podstatné: pôvodne tento stack nemal žiadnu výškovú
 * väzbu a rástol z 28 px na 210 px podľa toho, či je hrana zapnutá — celý
 * detail panel tým skákal o 80 px. Teraz je stredný riadok nákresu presne
 * `FRAME_H` bez ohľadu na stav hrán. Stĺpec popisu je tam aj pri vypnutej
 * hrane, aby chipy neposkočili.
 */
function VEdgeLabel({
  label,
  edge,
  onToggle,
  onPick,
  onQuick,
  stav,
  rucne = false,
  strana,
}: {
  label: string;
  edge: EdgeState;
  onToggle: () => void;
  onPick: () => void;
  onQuick?: (mm: string) => void;
  /** Stav uloženej hrany strany z backendu (`zla_sirka`…), KOLO 135. */
  stav?: string;
  /** Hrana je mimo hrán materiálu — vybratá ručne (KOLO 177). */
  rucne?: boolean;
  /** Na ktorej strane nákresu — popis ide na vonkajšiu stranu. */
  strana: "vlavo" | "vpravo";
}): JSX.Element {
  const enabled = edge.auto || !!edge.id;
  const info = useEdgeInfo(edge);
  const st = stavStitku(stav);

  const ovladanie = (
    <div className="flex w-8 flex-col items-center justify-center gap-0.5">
      <input
        type="checkbox"
        checked={enabled}
        onChange={onToggle}
        title="Zapnúť / vypnúť hranu"
        aria-label={`Zapnúť / vypnúť hranu ${label}`}
        className="h-3.5 w-3.5 accent-primary cursor-pointer"
      />
      <span className="text-[10px] font-mono text-muted-foreground">
        {label}
      </span>
      {enabled && onQuick ? (
        <ThicknessQuick
          label={label}
          current={info.hrubka}
          onPick={onQuick}
          vertical
        />
      ) : null}
      {enabled ? <HranaOdkaz odkaz={info.odkaz} label={label} /> : null}
    </div>
  );
  const popis = (
    <div
      className="flex h-full w-4 items-center justify-center"
      data-testid={`edge-popis-${label.toLowerCase()}`}
    >
      {enabled ? (
        <button
          type="button"
          onClick={onPick}
          className={cn(
            "truncate font-mono text-[11px] leading-4 hover:underline cursor-pointer",
            st.trieda,
          )}
          style={{ writingMode: "vertical-lr", maxHeight: FRAME_H }}
          title={`${st.title}${info.label}${rucne ? RUCNE_TITLE : ""} — klik: vybrať hranu`}
        >
          {stav === STAV_ZLA_SIRKA ? "! " : null}
          {info.label}
          {rucne ? " · ručne" : null}
        </button>
      ) : null}
    </div>
  );

  return (
    <div
      className="flex w-14 items-center justify-center gap-0.5"
      style={{ height: FRAME_H }}
      data-testid={`edge-label-${label.toLowerCase()}`}
    >
      {strana === "vlavo" ? popis : ovladanie}
      {strana === "vlavo" ? ovladanie : popis}
    </div>
  );
}

/** Vysvetlenie štítku „ručne" — prečo zmena materiálu hranu nechá. */
const RUCNE_TITLE =
  " · ručne vybratá (nie je medzi hranami materiálu) — zmena materiálu ju nechá";

/** Stručný štítok hrany pre vizuál: "22×1 · 261375" alebo "auto"
 * + reálna hrúbka hrany (pre zvýraznenie rýchleho prepínača). */
export function useEdgeInfo(edge: EdgeState): {
  label: string;
  hrubka: number | null;
  /** Odkaz k dodávateľovi (Hranipex) — skladá ho backend (KOLO 135). */
  odkaz: string | null;
} {
  const q = useMaterialDetail(edge.id ?? undefined);
  if (!edge.id) {
    return { label: edge.auto ? "auto" : "—", hrubka: null, odkaz: null };
  }
  const m = q.data?.data;
  if (!m) {
    // Detail ešte nenačítaný — kratký fallback
    return { label: edge.id.slice(0, 8), hrubka: null, odkaz: null };
  }
  const id =
    m.daex_id ?? m.hranipex_id ?? m.jafholz_id ?? m.twd_id ?? m.hafele_id ?? null;
  const w = m.sirka_mm != null ? parseFloat(String(m.sirka_mm)) : null;
  const h = m.hrubka_mm != null ? parseFloat(String(m.hrubka_mm)) : null;
  const dim = w && h ? `${w}×${h}` : "";
  let label: string;
  if (dim && id) label = `${dim} · ${id}`;
  else if (dim) label = dim;
  else label = id ?? edge.id.slice(0, 8);
  return { label, hrubka: h, odkaz: m.eshop_odkaz ?? null };
}

/** Malý odkaz ↗ na hranu u dodávateľa — skontrolovať ju na Hranipexe. */
export function HranaOdkaz({
  odkaz,
  label,
}: {
  odkaz: string | null;
  label: string;
}): JSX.Element | null {
  if (!odkaz) return null;
  return (
    <a
      href={odkaz}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Hrana ${label} u dodávateľa`}
      title="Otvoriť hranu u dodávateľa (Hranipex)"
      className="text-[10px] text-muted-foreground hover:text-primary"
      onClick={(e) => e.stopPropagation()}
    >
      ↗
    </a>
  );
}

/** Text a farba štítku hrany podľa stavu z backendu (KOLO 135). */
function stavStitku(stav: string | undefined): { trieda: string; title: string } {
  if (stav === STAV_ZLA_SIRKA) {
    return {
      trieda: "text-destructive font-semibold",
      title: "Hrana je úzka na hrúbku dosky — vymeň ju. ",
    };
  }
  return { trieda: "text-primary", title: "" };
}

/**
 * Rýchly prepínač hrúbky hrany — 0.5 / 1 / 2 mm. Klik auto-priradí
 * hranu materiálu bez otvárania pickera; aktívna hrúbka je zvýraznená.
 */
function ThicknessQuick({
  label,
  current,
  onPick,
  vertical = false,
}: {
  label: string;
  current: number | null;
  onPick: (mm: string) => void;
  vertical?: boolean;
}): JSX.Element {
  const active = hrubkaBucket(current);
  const nahradna = jeNahradnaHrubka(current);
  return (
    <div
      className={cn(
        "flex gap-0.5",
        vertical ? "flex-col items-center" : "items-center",
      )}
    >
      {QUICK_HRUBKY.map((mm) => (
        <button
          key={mm}
          type="button"
          onClick={() => onPick(mm)}
          aria-label={`${label} hrana ${mm} mm`}
          title={
            active === mm && nahradna
              ? `V dekore je ${current} mm — ${mm} mm tam nie je`
              : `Priradiť ${mm} mm hranu`
          }
          className={cn(
            "rounded border px-1 py-0 text-[9px] font-mono leading-4 transition-colors",
            active === mm
              ? HRANA_FARBY[mm]?.chip ??
                  "bg-primary text-primary-foreground border-primary"
              : "border-input text-muted-foreground hover:bg-accent",
          )}
        >
          {mm}
          {active === mm && nahradna ? "≠" : null}
        </button>
      ))}
    </div>
  );
}
